import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, message, Alert, Button, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Composants
import UserFilters from '../../components/Admin/UserFilters';
import UserStats from '../../components/Admin/UserStats';
import UsersTable from '../../components/Admin/UsersTables';
import UserModals from '../../components/Admin/UserModals';

const { Title, Text } = Typography;

const AdminUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUsers, updateUserRole, updateUserStatus, loading } = useAdmin();
  
  // États
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ 
    page: 1, 
    limit: 50, 
    total: 0 
  });
  const [filters, setFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs
  const isLoadingRef = useRef(false);
  const mountedRef = useRef(true);

  // Fonction pour charger les utilisateurs
  const loadUsers = useCallback(async () => {
    if (isLoadingRef.current) return;
    
    if (!mountedRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      
      const result = await getUsers(params);
      
      
      if (!mountedRef.current) return;
      
      if (result && result.success) {
        const responseData = result.data || result;
        const usersList = responseData.users || responseData.data || [];
        const paginationData = responseData.pagination || {};
        
        setUsers(usersList);
        setPagination({
          page: paginationData.page || pagination.page,
          limit: paginationData.limit || pagination.limit,
          total: paginationData.total || usersList.length || 0
        });
      } else {
        setError(result?.message || t('admin.users_management.messages.load_error'));
        message.error(result?.message || t('admin.users_management.messages.load_error'));
      }
    } catch (error) {
      if (mountedRef.current) {
        setError(error.message);
        message.error(t('admin.users_management.messages.load_error'));
      }
    } finally {
      if (mountedRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [getUsers, pagination.page, pagination.limit, filters, t]);

  // Effet pour charger les utilisateurs
  useEffect(() => {
    mountedRef.current = true;
    
    loadUsers();
    
    return () => {
      mountedRef.current = false;
    };
  }, [loadUsers]);

  // Handlers
  const handleTableChange = (newPagination, filters, sorter) => {
    
    setPagination(prev => ({
      ...prev,
      page: newPagination.current,
      limit: newPagination.pageSize
    }));
    
    // Gérer les filtres de colonnes
    const newFilters = { ...filters };
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === null) {
        delete newFilters[key];
      } else if (Array.isArray(newFilters[key])) {
        newFilters[key] = newFilters[key][0];
      }
    });
    
    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
  };

  const handleSearch = (searchParams) => {
    setFilters(searchParams);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewUser = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleEditRole = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({ role: user.role });
    setRoleModalVisible(true);
  };

  const handleUpdateStatus = (user, status) => {
    setSelectedUser(user);
    form.setFieldsValue({ status });
    setStatusModalVisible(true);
  };

  const handleRoleUpdate = async () => {
    try {
      const values = await form.validateFields();
      const result = await updateUserRole(selectedUser.id, values.role);
      
      if (result?.success) {
        message.success(t('admin.users_management.messages.role_success'));
        setRoleModalVisible(false);
        
        // Mettre à jour localement
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === selectedUser.id ? { ...u, role: values.role } : u
          )
        );
        
        // Recharger les données
        loadUsers();
      } else {
        message.error(result?.message || t('admin.users_management.messages.error'));
      }
    } catch (error) {
      console.error('❌ Erreur handleRoleUpdate:', error);
      message.error(t('admin.users_management.messages.error'));
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const values = await form.validateFields();
      const result = await updateUserStatus(selectedUser.id, values.status);
      
      if (result?.success) {
        message.success(t('admin.users_management.messages.status_success'));
        setStatusModalVisible(false);
        
        // Mettre à jour localement
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === selectedUser.id ? { 
              ...u, 
              account_status: values.status 
            } : u
          )
        );
        
        // Recharger les données
        loadUsers();
      } else {
        message.error(result?.message || t('admin.users_management.messages.error'));
      }
    } catch (error) {
      console.error('❌ Erreur handleStatusUpdate:', error);
      message.error(t('admin.users_management.messages.error'));
    }
  };

  const handleRefresh = () => {
    loadUsers();
  };

  const handleUserUpdated = (updatedUser) => {
  // Mettre à jour la liste des utilisateurs
  setUsers(prevUsers => 
    prevUsers.map(u => 
      u.id === updatedUser.id ? { ...u, ...updatedUser } : u
    )
  );
  
  message.success(t('admin.users_management.messages.update_success'));
};
  return (
    <div className="admin-users-inner animate-fade-in">
      <div className="glass-header">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <div>
            <Title level={2} className="admin-page-title">
              {t('admin.users_management.title')}
            </Title>
            <Text className="admin-page-subtitle">{t('admin.users_management.subtitle')}</Text>
          </div>
          
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
            className="glass-button"
          >
            {t('admin.users_management.refresh')}
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <UserStats 
        users={users} 
        loading={isLoading} 
      />
      
      {/* Filtres */}
      <div style={{ marginBottom: '24px' }}>
        <UserFilters
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          loading={isLoading}
        />
      </div>
      
      {/* Message d'erreur */}
      {error && (
        <Alert
          message={t('admin.users_management.messages.error')}
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: '24px' }}
        />
      )}
      
      {/* Table */}
      <UsersTable
  users={users}
  pagination={pagination}
  loading={isLoading || loading}
  onView={handleViewUser}
  onEditRole={handleEditRole}
  onUpdateStatus={handleUpdateStatus}
  onTableChange={handleTableChange}
  onUserUpdated={handleUserUpdated} // Ajouter cette prop
/>
      
      {/* Modals */}
      <UserModals
        selectedUser={selectedUser}
        roleModalVisible={roleModalVisible}
        statusModalVisible={statusModalVisible}
        loading={loading}
        onRoleModalClose={() => setRoleModalVisible(false)}
        onStatusModalClose={() => setStatusModalVisible(false)}
        onRoleUpdate={handleRoleUpdate}
        onStatusUpdate={handleStatusUpdate}
        form={form}
      />
    </div>
  );
};

export default AdminUsers;